// Post-processing chain: bloom, tilt-shift, painterly filter, vignette.
import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { HorizontalTiltShiftShader } from 'three/addons/shaders/HorizontalTiltShiftShader.js';
import { VerticalTiltShiftShader } from 'three/addons/shaders/VerticalTiltShiftShader.js';
import { renderer } from './renderer.js';
import { cam, scene } from './scene.js';

export const composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, cam));
// no SSAO: GTAO re-rendered the whole scene and halved the frame rate for a barely visible gain under the paint filter
export const bloom = new UnrealBloomPass(new THREE.Vector2(innerWidth, innerHeight), 0, 0.5, 0.8);
composer.addPass(bloom);
// tilt-shift blur: sells the "miniature" look
export const tiltH = new ShaderPass(HorizontalTiltShiftShader),
  tiltV = new ShaderPass(VerticalTiltShiftShader);
export function setTilt(focus, amount) {
  tiltH.uniforms.h.value = amount / innerWidth;
  tiltV.uniforms.v.value = amount / innerHeight;
  tiltH.uniforms.r.value = tiltV.uniforms.r.value = focus;
}
composer.addPass(tiltH);
composer.addPass(tiltV);
composer.addPass(new OutputPass());
const vignette = new ShaderPass({
  uniforms: { tDiffuse: { value: null } },
  vertexShader:
    'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
  fragmentShader:
    'uniform sampler2D tDiffuse; varying vec2 vUv; void main(){ vec4 c = texture2D(tDiffuse, vUv); vec2 d = vUv - .5; c.rgb *= mix(1.0, .86, smoothstep(.3, .9, dot(d, d) * 2.2)); float l = dot(c.rgb, vec3(.299,.587,.114)); c.rgb = mix(vec3(l), c.rgb, 1.28); c.rgb = c.rgb * .95 + vec3(.02, .035, .05) * (1.0 - l); c.rgb *= vec3(1.02, 1.01, .97); gl_FragColor = c; }',
});
// Kuwahara filter: flattens texture noise into painted strokes while keeping edges
export const paint = new ShaderPass({
  uniforms: {
    tDiffuse: { value: null },
    px: { value: new THREE.Vector2(1 / innerWidth, 1 / innerHeight) },
  },
  vertexShader:
    'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
  fragmentShader: `uniform sampler2D tDiffuse; uniform vec2 px; varying vec2 vUv;
    void main() {
      vec3 m0 = vec3(0.), m1 = vec3(0.), m2 = vec3(0.), m3 = vec3(0.), s0 = vec3(0.), s1 = vec3(0.), s2 = vec3(0.), s3 = vec3(0.);
      for (int j = 0; j <= 2; j++) for (int i = 0; i <= 2; i++) {
        vec2 o = vec2(float(i), float(j)) * px;
        vec3 a = texture2D(tDiffuse, vUv + vec2(-o.x, -o.y)).rgb, b = texture2D(tDiffuse, vUv + vec2(o.x, -o.y)).rgb;
        vec3 c = texture2D(tDiffuse, vUv + vec2(-o.x, o.y)).rgb, d = texture2D(tDiffuse, vUv + vec2(o.x, o.y)).rgb;
        m0 += a; s0 += a * a; m1 += b; s1 += b * b; m2 += c; s2 += c * c; m3 += d; s3 += d * d;
      }
      m0 /= 9.; m1 /= 9.; m2 /= 9.; m3 /= 9.;
      float v0 = dot(s0 / 9. - m0 * m0, vec3(1.)), v1 = dot(s1 / 9. - m1 * m1, vec3(1.)), v2 = dot(s2 / 9. - m2 * m2, vec3(1.)), v3 = dot(s3 / 9. - m3 * m3, vec3(1.));
      vec3 r = m0; float v = v0; if (v1 < v) { v = v1; r = m1; } if (v2 < v) { v = v2; r = m2; } if (v3 < v) { r = m3; }
      // ink lines: Sobel edges of the original image keep every window, railing and leaf readable
      vec3 W = vec3(.3, .59, .11); vec3 o = texture2D(tDiffuse, vUv).rgb;
      float tl = dot(texture2D(tDiffuse, vUv + px * vec2(-1., 1.)).rgb, W), t = dot(texture2D(tDiffuse, vUv + px * vec2(0., 1.)).rgb, W), tr = dot(texture2D(tDiffuse, vUv + px * vec2(1., 1.)).rgb, W);
      float l = dot(texture2D(tDiffuse, vUv + px * vec2(-1., 0.)).rgb, W), rr = dot(texture2D(tDiffuse, vUv + px * vec2(1., 0.)).rgb, W);
      float bl = dot(texture2D(tDiffuse, vUv + px * vec2(-1., -1.)).rgb, W), b = dot(texture2D(tDiffuse, vUv + px * vec2(0., -1.)).rgb, W), br = dot(texture2D(tDiffuse, vUv + px * vec2(1., -1.)).rgb, W);
      float gx = tr + 2. * rr + br - tl - 2. * l - bl, gy = tl + 2. * t + tr - bl - 2. * b - br;
      float ink = smoothstep(.2, .5, length(vec2(gx, gy)));
      vec3 col = mix(o, r, .6) * (1.0 - ink * .34);
      gl_FragColor = vec4(col, 1.0);
    }`,
});
composer.addPass(paint);
composer.addPass(vignette);
